import { useEffect, useRef, useState } from 'react';
import { useReducedMotion } from './useReducedMotion';

/**
 * Animates a number to `target` with requestAnimationFrame and an ease-out curve.
 * Returns the current (possibly fractional) value — the caller rounds/formats it.
 *
 *  - On mount it counts up from 0 → target (the intro flourish).
 *  - When `target` later changes (e.g. a background refetch returns new minutes), it
 *    animates from the value currently on screen → the new target, NOT from 0. Resetting
 *    to 0 on every refetch made the figure flicker as if the data had been lost.
 *
 * Respects reduced-motion: returns `target` immediately, no animation.
 */
export const useCountUp = (target: number, durationMs = 900): number => {
  const reduced = useReducedMotion();
  const [value, setValue] = useState(reduced ? target : 0);
  const frameRef = useRef<number>();
  // Track the latest rendered value so a target change animates from where we are now.
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    if (reduced || durationMs <= 0) {
      setValue(target);
      return;
    }

    const from = valueRef.current;
    const delta = target - from;
    if (delta === 0) return;

    let start: number | undefined;

    const tick = (now: number) => {
      if (start === undefined) start = now;
      const elapsed = now - start;
      const t = Math.min(1, elapsed / durationMs);
      const eased = 1 - Math.pow(1 - t, 3); // easeOutCubic
      setValue(from + delta * eased);
      if (t < 1) {
        frameRef.current = requestAnimationFrame(tick);
      } else {
        setValue(target);
      }
    };

    frameRef.current = requestAnimationFrame(tick);

    return () => {
      if (frameRef.current) cancelAnimationFrame(frameRef.current);
    };
  }, [target, durationMs, reduced]);

  return value;
};
